<template>
  <div class="letter-navigation">
    <div class="letter-navigation__letters-container">
      <ul :class="listClasses">
        <li
          v-for="letter in LETTERS"
          :key="letter"
          class="letter-navigation__link-container"
        >
          <a-link
            :to="generateTermLetterLink(letter)"
            :class="getLinkClasses(letter)"
            >{{ letter }}</a-link
          >
        </li>

        <a-visibility show :on="targetBreakpoints">
          <li class="letter-navigation__divider" />
        </a-visibility>

        <a-visibility show :on="targetBreakpoints">
          <li class="letter-navigation__search">
            <a-quicksearch :type="QUICKSEARCH_TYPE.TERMS" />
          </li>
        </a-visibility>
      </ul>

      <div :class="buttonClasses" @click="toggleNavigation" />
    </div>

    <a-visibility hide :to="$breakpoint.desktopMd">
      <div class="letter-navigation__divider" />
    </a-visibility>

    <a-visibility hide :on="targetBreakpoints">
      <div class="letter-navigation__search">
        <a-quicksearch :type="QUICKSEARCH_TYPE.TERMS" />
      </div>
    </a-visibility>
  </div>
</template>

<script>
import { ROUTE_NAME } from 'enums/routes'
import AQuicksearch, { QUICKSEARCH_TYPE } from 'shared/AQuickSearch'

export const LETTERS = 'abcdefghijklmnopqrstuvwxyz'.split('')

export default {
  name: 'ALetterNavigation',
  components: { AQuicksearch },
  data() {
    return {
      LETTERS,
      QUICKSEARCH_TYPE,
      searchPhrase: '',
      isExpanded: false
    }
  },
  computed: {
    targetBreakpoints() {
      return [this.$breakpoint.tablet, this.$breakpoint.desktopSm]
    },

    listClasses() {
      return {
        'letter-navigation__list': true,
        'letter-navigation__list_expanded': this.isExpanded
      }
    },
    buttonClasses() {
      return {
        'letter-navigation__show-more': true,
        'letter-navigation__show-more_expanded': this.isExpanded
      }
    }
  },
  methods: {
    getLinkClasses(letter) {
      return {
        'letter-navigation__link': true,
        'letter-navigation__link_active':
          letter === (this.$route.params.letter || '').toLowerCase()
      }
    },
    toggleNavigation() {
      this.isExpanded = !this.isExpanded
    },
    generateTermLetterLink(letter) {
      return { name: ROUTE_NAME.TERMS_LETTER, params: { letter } }
    }
  }
}
</script>

<style lang="scss" scoped>
$collapse-width-lg: 1683px;
$collapse-width-md: 1340px;
$tablet-max-width: 1023px;
$tablet-search-fix-width: 944px;
$arrow-width: 12px;

.letter-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background-color: $c--gray-cloud;

  @include desktop-md {
    flex-direction: column;
    padding: 10px 20px 30px;
  }

  @include desktop-sm {
    padding: 10px 20px;
  }

  @media (min-width: $tablet-search-fix-width) and (max-width: $tablet-max-width) {
    .letter-navigation__divider {
      display: none;
    }

    .letter-navigation__search {
      max-width: 535px;
      margin: 10px auto 0;
    }
  }

  @include mobile {
    margin-top: 0;
    margin-left: -$mobile-body-padding;
    margin-right: -$mobile-body-padding;
    padding: 10px 20px 20px;
  }

  &__letters-container {
    position: relative;
    max-width: 1065px;
    width: 100%;

    @include desktop-md {
      justify-content: center;
      margin-bottom: 10px;
    }
  }

  &__list {
    position: relative;
    width: 100%;
    height: 60px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    transition: height 0.3s;

    @include desktop-md {
      @media (max-width: $collapse-width-md) {
        width: calc(100% - #{$arrow-width});
      }
    }

    @include desktop-sm {
      width: 100%;
      height: auto;
      overflow: visible;
    }

    @include mobile {
      width: calc(100% - #{$arrow-width});
      height: 36px;
      justify-content: flex-start;
      overflow: hidden;
    }

    &_expanded {
      height: 60px;
      overflow: hidden;
      width: 100%;

      @media (max-width: $collapse-width-lg) {
        height: auto;
        overflow: auto;
        justify-content: flex-start;
      }

      .letter-navigation__link-container:last-child {
        margin-right: $arrow-width;
      }
    }
  }

  &__link {
    display: block;
    font-size: 28px;

    text-transform: uppercase;
    padding: 16px 8px;

    &:focus {
      color: $c--gray-main;
    }

    &:hover,
    &_active {
      background-color: $c--main;
      color: white;

      &:focus {
        color: white;
      }
    }

    @include mobile {
      font-size: 19px;
      padding: 9px 8px 8px;
    }
  }

  &__divider {
    height: 40px;
    width: 2px;
    margin: 0 25px;
    background-color: $c--gray;
  }

  &__search {
    min-width: 265px;
    max-width: 535px;

    @include desktop-md {
      width: 100%;
    }

    @include desktop-sm {
      width: auto;
      max-width: none;
      flex-grow: 1;
    }

    @include mobile {
      width: 100%;
    }
  }

  &__show-more {
    display: none;
    content: '';
    position: absolute;
    cursor: pointer;
    bottom: 25px;
    right: -10px;
    width: 10px;
    height: 10px;
    border-left: 3px solid black;
    border-bottom: 3px solid black;
    transform: rotate(-45deg);
    transition: transform 0.3s;

    &_expanded {
      transform: rotate(135deg);
    }

    @media (max-width: $collapse-width-lg) {
      display: block;
    }

    @include desktop-md {
      display: none;

      @media (max-width: $collapse-width-md) {
        display: block;
      }
    }

    @include desktop-sm {
      display: none;
    }

    @include mobile {
      display: block;
      bottom: 15px;
      right: 0;
      border-left: 2px solid black;
      border-bottom: 2px solid black;
    }

    @media (max-width: 320px) {
      right: 10px;
    }
  }
}
</style>
